import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { theme } from '../../styles/theme';

const AuthModal = ({ isOpen, onClose, initialMode }) => {
  const { login, register, resetPassword, error: authError } = useAuth();
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [isForgotPasswordMode, setIsForgotPasswordMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [mounted, setMounted] = useState(false);

  // Login form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  
  // Forgot password form state
  const [resetEmail, setResetEmail] = useState('');

  // Register form state
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [state, setState] = useState('');
  const [company, setCompany] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [taxId, setTaxId] = useState('');
  const [resaleCertificate, setResaleCertificate] = useState(null);

  // US States list
  const US_STATES = [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
    'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
    'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
    'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
    'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
    'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
    'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
    'Wisconsin', 'Wyoming'
  ];

  // Callers can open straight into a tab ('login' | 'register'); without it
  // the modal keeps whichever tab was last used.
  useEffect(() => {
    if (isOpen && initialMode) {
      setIsLoginMode(initialMode !== 'register');
      setIsForgotPasswordMode(false);
    }
  }, [isOpen, initialMode]);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      // Prevent body scroll on mobile when modal is open
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      // Restore body scroll
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }
    
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [isOpen]);

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setResetEmail('');
    setRegisterEmail('');
    setRegisterPassword('');
    setConfirmPassword('');
    setFirstName('');
    setLastName('');
    setPhone('');
    setState('');
    setCompany('');
    setBusinessName('');
    setTaxId('');
    setResaleCertificate(null);
    setError('');
    setSuccess('');
    setShowPassword(false);
    setShowConfirmPassword(false);
    setIsForgotPasswordMode(false);
  };

  const handleClose = () => {
    setMounted(false);
    setTimeout(() => {
      resetForm();
      onClose();
    }, 300);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);
      if (result.success) {
        setSuccess('Login successful!');
        setTimeout(() => {
          handleClose();
        }, 1000);
      } else {
        setError(result.error || 'Login failed');
      }
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (registerPassword !== confirmPassword) {
      setError('Passwords do not match');
      // Scroll to top to show error
      setTimeout(() => {
        const modalContent = document.querySelector('.auth-modal-content');
        if (modalContent) {
          modalContent.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 100);
      return;
    }

    // Stronger password validation
    if (registerPassword.length < 8) {
      setError('Password must be at least 8 characters');
      // Scroll to top to show error
      setTimeout(() => {
        const modalContent = document.querySelector('.auth-modal-content');
        if (modalContent) {
          modalContent.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 100);
      return;
    }
    
    // Check for password complexity
    const hasUpperCase = /[A-Z]/.test(registerPassword);
    const hasLowerCase = /[a-z]/.test(registerPassword);
    const hasNumber = /\d/.test(registerPassword);
    
    if (!hasUpperCase || !hasLowerCase || !hasNumber) {
      setError('Password must contain at least one uppercase letter, one lowercase letter, and one number');
      // Scroll to top to show error
      setTimeout(() => {
        const modalContent = document.querySelector('.auth-modal-content');
        if (modalContent) {
          modalContent.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 100);
      return;
    }

    if (!firstName || !lastName || !phone || !state) {
      setError('Please fill in all required fields');
      // Scroll to top to show error
      setTimeout(() => {
        const modalContent = document.querySelector('.auth-modal-content');
        if (modalContent) {
          modalContent.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 100);
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('email', registerEmail);
      formData.append('password', registerPassword);
      formData.append('first_name', firstName);
      formData.append('last_name', lastName);
      formData.append('phone', phone);
      formData.append('state', state);
      
      if (company) formData.append('company', company);
      if (businessName) formData.append('business_name', businessName);
      if (taxId) formData.append('tax_id', taxId);
      if (resaleCertificate) formData.append('resale_certificate', resaleCertificate);

      const result = await register(formData);
      
      if (result.success) {
        setSuccess('Registration successful! You can now login.');
        // Scroll to top to show success message
        setTimeout(() => {
          const modalContent = document.querySelector('.auth-modal-content');
          if (modalContent) {
            modalContent.scrollTo({ top: 0, behavior: 'smooth' });
          }
        }, 100);
        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        setError(result.error || 'Registration failed');
        // Scroll to top to show error
        setTimeout(() => {
          const modalContent = document.querySelector('.auth-modal-content');
          if (modalContent) {
            modalContent.scrollTo({ top: 0, behavior: 'smooth' });
          }
        }, 100);
      }
    } catch (err) {
      setError(err.message || 'Registration failed');
      // Scroll to top to show error
      setTimeout(() => {
        const modalContent = document.querySelector('.auth-modal-content');
        if (modalContent) {
          modalContent.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 100);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const result = await resetPassword(resetEmail);
      if (result.success) {
        setSuccess('Password reset email sent! Please check your inbox.');
        setTimeout(() => {
          setIsForgotPasswordMode(false);
          setIsLoginMode(true);
          setResetEmail('');
          setSuccess('');
        }, 3000);
      } else {
        setError(result.error || 'Failed to send reset email');
      }
    } catch (err) {
      setError(err.message || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        e.target.value = '';
        return;
      }
      setResaleCertificate(file);
    }
  };

  if (!isOpen) return null;

  // Password strength calculator
  const getPasswordStrength = (pass) => {
    if (!pass) return { level: 0, text: '', color: '' };
    let strength = 0;
    if (pass.length >= 8) strength++;
    if (pass.length >= 12) strength++;
    if (/[a-z]/.test(pass) && /[A-Z]/.test(pass)) strength++;
    if (/\d/.test(pass)) strength++;
    if (/[^a-zA-Z0-9]/.test(pass)) strength++;
    
    if (strength <= 2) return { level: strength, text: 'Weak', color: theme.colors.error };
    if (strength <= 3) return { level: strength, text: 'Fair', color: theme.colors.warning };
    return { level: strength, text: 'Strong', color: theme.colors.success };
  };

  const passwordStrength = isLoginMode ? null : getPasswordStrength(registerPassword);

  // Styles with modern animations
  const overlayStyles = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.75) 0%, rgba(0, 0, 0, 0.6) 100%)',
    backdropFilter: 'blur(10px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: theme.zIndex.modal,
    padding: 'clamp(8px, 2vw, 16px)',
    animation: mounted ? 'fadeIn 0.4s cubic-bezier(0.4, 0, 0.2, 1)' : 'fadeOut 0.3s ease-out',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
  };

  const modalStyles = {
    backgroundColor: theme.colors.surface,
    borderRadius: '24px',
    boxShadow: '0 30px 60px -12px rgba(139, 69, 19, 0.35), 0 0 0 1px rgba(139, 69, 19, 0.1)',
    maxWidth: '500px',
    width: '100%',
    maxHeight: '95vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    animation: mounted ? 'slideUpModal 0.4s cubic-bezier(0.4, 0, 0.2, 1)' : 'slideDownModal 0.3s ease-out',
    position: 'relative',
  };

  const headerStyles = {
    padding: 'clamp(16px, 4vw, 28px) clamp(16px, 4vw, 24px)',
    background: `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primaryDark} 100%)`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexShrink: 0,
  };

  const titleStyles = {
    fontFamily: theme.typography.fontFamily.primary,
    fontSize: 'clamp(1.25rem, 5vw, 2rem)',
    fontWeight: theme.typography.fontWeight.bold,
    color: theme.colors.surface,
    margin: 0,
    letterSpacing: '0.02em',
    textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
  };

  const closeButtonStyles = {
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: theme.borderRadius.round,
    fontSize: 'clamp(1.25rem, 3vw, 1.5rem)',
    cursor: 'pointer',
    color: theme.colors.surface,
    padding: 'clamp(6px, 1.5vw, 8px)',
    lineHeight: 1,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    minWidth: 'clamp(40px, 10vw, 44px)',
    minHeight: 'clamp(40px, 10vw, 44px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    fontWeight: 'bold',
  };

  const contentStyles = {
    padding: 'clamp(16px, 4vw, 24px)',
    overflowY: 'auto',
    flex: 1,
    WebkitOverflowScrolling: 'touch',
    scrollPaddingTop: '20px',
    scrollPaddingBottom: '20px',
  };

  const formStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: 'clamp(12px, 3vw, 16px)',
  };

  const inputGroupStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: 'clamp(6px, 2vw, 8px)',
    position: 'relative',
  };

  const labelStyles = {
    fontSize: 'clamp(0.875rem, 2vw, 0.875rem)',
    fontWeight: theme.typography.fontWeight.bold,
    color: theme.colors.text,
    fontFamily: theme.typography.fontFamily.secondary,
    letterSpacing: '0.02em',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  };

  const inputStyles = {
    padding: 'clamp(12px, 3vw, 16px) clamp(14px, 3vw, 18px)',
    border: `2px solid ${theme.colors.border}`,
    borderRadius: 'clamp(8px, 2vw, 12px)',
    fontSize: 'clamp(0.875rem, 2vw, 1rem)',
    fontFamily: theme.typography.fontFamily.secondary,
    color: theme.colors.text,
    backgroundColor: theme.colors.surface,
    outline: 'none',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    minHeight: 'clamp(48px, 10vw, 56px)',
    boxShadow: '0 2px 8px rgba(139, 69, 19, 0.05)',
  };

  const inputWrapperStyles = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  };

  const passwordToggleStyles = {
    position: 'absolute',
    right: 'clamp(8px, 2vw, 12px)',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: 'clamp(6px, 1.5vw, 8px)',
    fontSize: 'clamp(1.125rem, 2.5vw, 1.25rem)',
    color: theme.colors.textSecondary,
    transition: theme.transitions.fast,
    minWidth: 'clamp(40px, 10vw, 44px)',
    minHeight: 'clamp(40px, 10vw, 44px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const errorStyles = {
    background: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)',
    color: theme.colors.error,
    padding: 'clamp(12px, 3vw, 16px) clamp(14px, 3vw, 18px)',
    borderRadius: 'clamp(8px, 2vw, 12px)',
    fontSize: 'clamp(0.8125rem, 2vw, 0.875rem)',
    border: `2px solid ${theme.colors.error}`,
    display: 'flex',
    alignItems: 'center',
    gap: 'clamp(8px, 2vw, 12px)',
    animation: 'shake 0.4s ease-in-out',
    boxShadow: '0 4px 12px rgba(239, 68, 68, 0.2)',
    fontWeight: theme.typography.fontWeight.medium,
  };

  const successStyles = {
    background: 'linear-gradient(135deg, #D1FAE5 0%, #A7F3D0 100%)',
    color: theme.colors.success,
    padding: 'clamp(12px, 3vw, 16px) clamp(14px, 3vw, 18px)',
    borderRadius: 'clamp(8px, 2vw, 12px)',
    fontSize: 'clamp(0.8125rem, 2vw, 0.875rem)',
    border: `2px solid ${theme.colors.success}`,
    display: 'flex',
    alignItems: 'center',
    gap: 'clamp(8px, 2vw, 12px)',
    animation: 'slideIn 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: '0 4px 12px rgba(34, 197, 94, 0.2)',
    fontWeight: theme.typography.fontWeight.medium,
  };

  const tabContainerStyles = {
    display: 'flex',
    gap: '8px',
    marginBottom: 'clamp(20px, 4vw, 28px)',
    borderBottom: `2px solid ${theme.colors.borderLight}`,
    padding: '0',
  };

  const tabButtonStyles = (isActive) => ({
    flex: 1,
    padding: 'clamp(12px, 3vw, 16px) clamp(16px, 4vw, 24px)',
    fontSize: 'clamp(0.9375rem, 2.5vw, 1.0625rem)',
    fontWeight: theme.typography.fontWeight.bold,
    fontFamily: theme.typography.fontFamily.secondary,
    color: isActive ? theme.colors.primary : theme.colors.textSecondary,
    background: isActive ? `linear-gradient(135deg, ${theme.colors.surfaceAlt} 0%, ${theme.colors.surface} 100%)` : 'transparent',
    border: 'none',
    borderBottom: isActive ? `3px solid ${theme.colors.primary}` : '3px solid transparent',
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    borderTopLeftRadius: '12px',
    borderTopRightRadius: '12px',
    position: 'relative',
    bottom: '-2px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'clamp(6px, 1.5vw, 8px)',
    minHeight: 'clamp(48px, 10vw, 56px)',
    letterSpacing: '0.02em',
  });

  const passwordStrengthBarStyles = {
    height: '4px',
    backgroundColor: theme.colors.borderLight,
    borderRadius: theme.borderRadius.sm,
    marginTop: theme.spacing.xs,
    overflow: 'hidden',
  };

  const passwordStrengthFillStyles = (strength) => ({
    height: '100%',
    backgroundColor: strength.color,
    width: `${(strength.level / 5) * 100}%`,
    transition: 'all 0.3s ease-in-out',
  });

  const fileInputLabelStyles = {
    display: 'inline-block',
    padding: 'clamp(12px, 3vw, 14px) clamp(14px, 3vw, 16px)',
    backgroundColor: theme.colors.background,
    border: `2px dashed ${theme.colors.border}`,
    borderRadius: 'clamp(8px, 2vw, 10px)',
    cursor: 'pointer',
    fontSize: 'clamp(0.8125rem, 2vw, 0.875rem)',
    color: theme.colors.textSecondary,
    transition: theme.transitions.base,
    textAlign: 'center',
    minHeight: 'clamp(44px, 10vw, 48px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  };

  const modernButtonStyles = {
    width: '100%',
    padding: 'clamp(14px, 3.5vw, 18px) clamp(20px, 5vw, 28px)',
    fontSize: 'clamp(0.9375rem, 2.5vw, 1.125rem)',
    fontWeight: theme.typography.fontWeight.bold,
    fontFamily: theme.typography.fontFamily.secondary,
    color: theme.colors.textOnDark,
    background: `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primaryDark} 100%)`,
    border: 'none',
    borderRadius: 'clamp(10px, 2.5vw, 14px)',
    cursor: loading ? 'not-allowed' : 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: '0 6px 20px rgba(139, 69, 19, 0.35)',
    position: 'relative',
    overflow: 'hidden',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    opacity: loading ? 0.7 : 1,
    minHeight: 'clamp(52px, 12vw, 58px)',
  };

  const modernButtonHoverStyles = {
    transform: 'translateY(-3px) scale(1.02)',
    boxShadow: '0 10px 30px rgba(139, 69, 19, 0.45)',
    background: `linear-gradient(135deg, ${theme.colors.primaryLight} 0%, ${theme.colors.primary} 100%)`,
  };

  const modernButtonActiveStyles = {
    transform: 'translateY(0) scale(0.98)',
    boxShadow: '0 4px 12px rgba(139, 69, 19, 0.3)',
  };

  return (
    <>
      <style>{`
        @keyframes fadeIn {
          from { 
            opacity: 0; 
          }
          to { 
            opacity: 1; 
          }
        }
        @keyframes fadeOut {
          from { opacity: 1; }
          to { opacity: 0; }
        }
        @keyframes slideUpModal {
          from { 
            opacity: 0;
            transform: translateY(30px) scale(0.9);
          }
          to { 
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        @keyframes slideDownModal {
          from { 
            opacity: 1;
            transform: translateY(0) scale(1);
          }
          to { 
            opacity: 0;
            transform: translateY(30px) scale(0.9);
          }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-8px); }
          20%, 40%, 60%, 80% { transform: translateX(8px); }
        }
        @keyframes slideIn {
          from { 
            opacity: 0;
            transform: translateY(-15px) scale(0.95);
          }
          to { 
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.8; }
        }
        @keyframes shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .auth-input:focus {
          border-color: ${theme.colors.primary};
          box-shadow: 0 0 0 4px rgba(139, 69, 19, 0.12), 0 4px 12px rgba(139, 69, 19, 0.15);
          transform: translateY(-2px);
        }
        .auth-input select,
        select.auth-input {
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%238B4513' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right clamp(12px, 3vw, 16px) center;
          padding-right: clamp(36px, 8vw, 44px);
        }
        .auth-close-btn:hover {
          transform: scale(1.1) rotate(90deg);
        }
        .auth-close-btn:active {
          transform: scale(0.95) rotate(90deg);
        }
        .auth-tab-button:hover:not([aria-selected="true"]) {
          background: ${theme.colors.surfaceAlt};
          color: ${theme.colors.text};
        }
        .auth-tab-button:active {
          transform: scale(0.98);
        }
        .auth-password-toggle:hover {
          color: ${theme.colors.primary};
          transform: scale(1.1);
        }
        .auth-file-label:hover {
          border-color: ${theme.colors.primary};
          background-color: ${theme.colors.surfaceAlt};
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(139, 69, 19, 0.15);
        }
        .modern-auth-button {
          position: relative;
          overflow: hidden;
        }
        .modern-auth-button::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
          transition: left 0.5s;
        }
        .modern-auth-button:hover::before {
          left: 100%;
        }
        .modern-auth-button:disabled {
          cursor: not-allowed;
          opacity: 0.7;
        }
        .modern-auth-button:disabled:hover {
          transform: none !important;
          box-shadow: 0 4px 15px rgba(139, 69, 19, 0.3) !important;
        }
        .loading-spinner {
          display: inline-block;
          width: 20px;
          height: 20px;
          border: 3px solid rgba(255, 255, 255, 0.3);
          border-top-color: #fff;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        /* Mobile-specific styles */
        @media (max-width: 768px) {
          .auth-input:focus {
            font-size: 16px !important; /* Prevents iOS zoom on focus */
          }
          .auth-modal-content {
            padding: 16px !important;
          }
        }

        @media (max-width: 480px) {
          .modern-auth-button:hover {
            transform: none !important;
          }
          .auth-close-btn:hover {
            transform: none !important;
          }
          .auth-modal-overlay {
            padding: 8px !important;
          }
          .auth-modal {
            border-radius: 16px !important;
            max-height: 97vh !important;
          }
        }

        /* Touch device optimizations */
        @media (hover: none) and (pointer: coarse) {
          .modern-auth-button:hover::before {
            display: none;
          }
          .auth-close-btn:hover,
          .auth-tab-button:hover,
          .auth-password-toggle:hover,
          .auth-file-label:hover {
            transform: none;
          }
          .auth-tab-button:not([aria-selected="true"]):active {
            background: ${theme.colors.surfaceAlt};
          }
        }

        /* Smooth transitions */
        * {
          -webkit-tap-highlight-color: transparent;
        }

        /* Ensure modal scales properly on very small screens */
        @media (max-width: 360px) {
          .auth-modal {
            width: calc(100vw - 16px) !important;
            max-height: 98vh !important;
            border-radius: 12px !important;
          }
          .auth-modal-content {
            padding: 12px !important;
          }
        }

        /* Fix iOS input zoom */
        @media screen and (-webkit-min-device-pixel-ratio: 0) {
          select, textarea, input {
            font-size: 16px !important;
          }
        }

        /* Add touch feedback for mobile */
        @media (hover: none) and (pointer: coarse) {
          .modern-auth-button:active {
            transform: scale(0.97) !important;
            opacity: 0.9;
          }
          .auth-close-btn:active {
            transform: scale(0.9) !important;
            opacity: 0.8;
          }
          .auth-password-toggle:active {
            transform: scale(0.9) !important;
            opacity: 0.7;
          }
        }

        /* Mobile modal positioning - slide from bottom on small screens */
        @media (max-width: 640px) {
          .auth-modal-overlay {
            align-items: flex-end !important;
            padding-bottom: 0 !important;
          }
          .auth-modal {
            border-bottom-left-radius: 0 !important;
            border-bottom-right-radius: 0 !important;
            margin-bottom: 0 !important;
          }
          .auth-tab-button {
            font-size: 0.875rem !important;
            padding: 12px 8px !important;
          }
          @keyframes slideUpModal {
            from { 
              opacity: 0;
              transform: translateY(100%);
            }
            to { 
              opacity: 1;
              transform: translateY(0);
            }
          }
          @keyframes slideDownModal {
            from { 
              opacity: 1;
              transform: translateY(0);
            }
            to { 
              opacity: 0;
              transform: translateY(100%);
            }
          }
        }
      `}</style>
      <div className="auth-modal-overlay" style={overlayStyles}>
        <div className="auth-modal" style={modalStyles}>
          {/* Header */}
          <div style={headerStyles}>
            <h2 style={titleStyles}>
              {isForgotPasswordMode ? 'Reset Password' : isLoginMode ? 'Welcome Back' : 'Create Account'}
            </h2>
            <button 
              className="auth-close-btn"
              style={closeButtonStyles} 
              onClick={handleClose}
              aria-label="Close"
              onTouchStart={(e) => {
                e.currentTarget.style.transform = 'scale(0.9)';
                e.currentTarget.style.opacity = '0.8';
              }}
              onTouchEnd={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.opacity = '1';
              }}
            >
              ×
            </button>
          </div>

          {/* Content */}
          <div className="auth-modal-content" style={contentStyles}>
            {/* Tab Navigation - Hide in forgot password mode */}
            {!isForgotPasswordMode && (
            <div style={tabContainerStyles}>
              <button
                className="auth-tab-button"
                style={tabButtonStyles(isLoginMode)}
                onClick={() => {
                  setIsLoginMode(true);
                  setError('');
                  setSuccess('');
                  setShowPassword(false);
                  setShowConfirmPassword(false);
                }}
                aria-selected={isLoginMode}
                role="tab"
                onTouchStart={(e) => {
                  if (!isLoginMode) {
                    e.currentTarget.style.transform = 'scale(0.98)';
                  }
                }}
                onTouchEnd={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <span>Sign In</span>
              </button>
              <button
                className="auth-tab-button"
                style={tabButtonStyles(!isLoginMode)}
                onClick={() => {
                  setIsLoginMode(false);
                  setError('');
                  setSuccess('');
                  setShowPassword(false);
                }}
                aria-selected={!isLoginMode}
                role="tab"
                onTouchStart={(e) => {
                  if (isLoginMode) {
                    e.currentTarget.style.transform = 'scale(0.98)';
                  }
                }}
                onTouchEnd={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <span>Create Account</span>
              </button>
            </div>
            )}

            {error && (
              <div style={errorStyles}>
                <span>⚠️</span>
                <span>{error}</span>
              </div>
            )}
            {success && (
              <div style={successStyles}>
                <span>✓</span>
                <span>{success}</span>
              </div>
            )}

          {isForgotPasswordMode ? (
            /* Forgot Password Form */
            <form onSubmit={handleForgotPassword} style={formStyles}>
              <p style={{
                fontSize: 'clamp(0.875rem, 2vw, 0.9375rem)',
                color: theme.colors.textSecondary,
                marginBottom: theme.spacing.md,
                lineHeight: '1.6',
              }}>
                Enter your email address and we'll send you a link to reset your password.
              </p>

              <div style={inputGroupStyles}>
                <label style={labelStyles} htmlFor="reset-email">
                  Email Address
                </label>
                <input
                  id="reset-email"
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                  className="auth-input"
                  style={inputStyles}
                  placeholder="your@email.com"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="modern-auth-button"
                style={{ 
                  ...modernButtonStyles,
                  marginTop: theme.spacing.md,
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    Object.assign(e.currentTarget.style, modernButtonHoverStyles);
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(139, 69, 19, 0.3)';
                    e.currentTarget.style.background = `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primaryDark} 100%)`;
                  }
                }}
                onMouseDown={(e) => {
                  if (!loading) {
                    Object.assign(e.currentTarget.style, modernButtonActiveStyles);
                  }
                }}
                onMouseUp={(e) => {
                  if (!loading) {
                    Object.assign(e.currentTarget.style, modernButtonHoverStyles);
                  }
                }}
                onTouchStart={(e) => {
                  if (!loading) {
                    e.currentTarget.style.transform = 'scale(0.97)';
                    e.currentTarget.style.opacity = '0.9';
                  }
                }}
                onTouchEnd={(e) => {
                  if (!loading) {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.opacity = '1';
                  }
                }}
              >
                {loading ? (
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: theme.spacing.md }}>
                    <span className="loading-spinner"></span>
                    Sending...
                  </span>
                ) : (
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: theme.spacing.sm }}>
                    Send Reset Link
                  </span>
                )}
              </button>

              <button
                type="button"
                onClick={() => {
                  setIsForgotPasswordMode(false);
                  setIsLoginMode(true);
                  setError('');
                  setSuccess('');
                  setResetEmail('');
                }}
                style={{
                  width: '100%',
                  padding: 'clamp(12px, 3vw, 14px)',
                  fontSize: 'clamp(0.875rem, 2vw, 0.9375rem)',
                  fontWeight: theme.typography.fontWeight.medium,
                  fontFamily: theme.typography.fontFamily.secondary,
                  color: theme.colors.primary,
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  transition: theme.transitions.base,
                  marginTop: theme.spacing.sm,
                }}
              >
                ← Back to Sign In
              </button>
            </form>
          ) : isLoginMode ? (
            /* Login Form */
            <form onSubmit={handleLogin} style={formStyles}>
              <div style={inputGroupStyles}>
                <label style={labelStyles} htmlFor="email">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="auth-input"
                  style={inputStyles}
                  placeholder="your@email.com"
                />
              </div>

              <div style={inputGroupStyles}>
                <label style={{...labelStyles, justifyContent: 'space-between'}} htmlFor="password">
                  <span>Password</span>
                  <button
                    type="button"
                    onClick={() => {
                      setIsForgotPasswordMode(true);
                      setIsLoginMode(false);
                      setError('');
                      setSuccess('');
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: theme.colors.primary,
                      fontSize: 'clamp(0.75rem, 1.8vw, 0.8125rem)',
                      fontWeight: theme.typography.fontWeight.medium,
                      cursor: 'pointer',
                      padding: 0,
                      textDecoration: 'underline',
                      transition: theme.transitions.fast,
                    }}
                  >
                    Forgot Password?
                  </button>
                </label>
                <div style={inputWrapperStyles}>
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="auth-input"
                    style={{...inputStyles, width: '100%', paddingRight: 'clamp(44px, 10vw, 54px)'}}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    className="auth-password-toggle"
                    style={passwordToggleStyles}
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="modern-auth-button"
                style={{ 
                  ...modernButtonStyles,
                  marginTop: theme.spacing.md,
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    Object.assign(e.currentTarget.style, modernButtonHoverStyles);
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(139, 69, 19, 0.3)';
                    e.currentTarget.style.background = `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primaryDark} 100%)`;
                  }
                }}
                onMouseDown={(e) => {
                  if (!loading) {
                    Object.assign(e.currentTarget.style, modernButtonActiveStyles);
                  }
                }}
                onMouseUp={(e) => {
                  if (!loading) {
                    Object.assign(e.currentTarget.style, modernButtonHoverStyles);
                  }
                }}
                onTouchStart={(e) => {
                  if (!loading) {
                    e.currentTarget.style.transform = 'scale(0.97)';
                    e.currentTarget.style.opacity = '0.9';
                  }
                }}
                onTouchEnd={(e) => {
                  if (!loading) {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.opacity = '1';
                  }
                }}
              >
                {loading ? (
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: theme.spacing.md }}>
                    <span className="loading-spinner"></span>
                    Signing In...
                  </span>
                ) : (
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: theme.spacing.sm }}>
                    Sign In
                  </span>
                )}
              </button>
            </form>
          ) : (
            /* Register Form */
            <form onSubmit={handleRegister} style={formStyles}>
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'clamp(10px, 2.5vw, 14px)'}}>
                <div style={inputGroupStyles}>
                  <label style={labelStyles} htmlFor="register-first-name">
                    First Name *
                  </label>
                  <input
                    id="register-first-name"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    className="auth-input"
                    style={inputStyles}
                    placeholder="John"
                  />
                </div>

                <div style={inputGroupStyles}>
                  <label style={labelStyles} htmlFor="register-last-name">
                    Last Name *
                  </label>
                  <input
                    id="register-last-name"
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    className="auth-input"
                    style={inputStyles}
                    placeholder="Doe"
                  />
                </div>
              </div>

              <div style={inputGroupStyles}>
                <label style={labelStyles} htmlFor="register-email">
                  Email Address *
                </label>
                <input
                  id="register-email"
                  type="email"
                  value={registerEmail}
                  onChange={(e) => setRegisterEmail(e.target.value)}
                  required
                  className="auth-input"
                  style={inputStyles}
                  placeholder="your@email.com"
                />
              </div>

              <div style={inputGroupStyles}>
                <label style={labelStyles} htmlFor="register-phone">
                  Phone Number *
                </label>
                <input
                  id="register-phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                  className="auth-input"
                  style={inputStyles}
                  placeholder="(555) 123-4567"
                />
              </div>

              <div style={inputGroupStyles}>
                <label style={labelStyles} htmlFor="register-state">
                  State *
                </label>
                <select
                  id="register-state"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  required
                  className="auth-input"
                  style={{...inputStyles, cursor: 'pointer'}}
                >
                  <option value="">Select a state</option>
                  {US_STATES.map((stateName) => (
                    <option key={stateName} value={stateName}>
                      {stateName}
                    </option>
                  ))}
                </select>
              </div>

              <div style={inputGroupStyles}>
                <label style={labelStyles} htmlFor="register-company">
                  Company (Optional)
                </label>
                <input
                  id="register-company"
                  type="text"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  className="auth-input"
                  style={inputStyles}
                  placeholder="Your Company LLC"
                />
              </div>

              <div style={inputGroupStyles}>
                <label style={labelStyles} htmlFor="register-password">
                  Password *
                </label>
                <div style={inputWrapperStyles}>
                  <input
                    id="register-password"
                    type={showPassword ? "text" : "password"}
                    value={registerPassword}
                    onChange={(e) => setRegisterPassword(e.target.value)}
                    required
                    minLength={8}
                    className="auth-input"
                    style={{...inputStyles, width: '100%', paddingRight: 'clamp(44px, 10vw, 54px)'}}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    className="auth-password-toggle"
                    style={passwordToggleStyles}
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
                {passwordStrength && registerPassword && (
                  <>
                    <div style={passwordStrengthBarStyles}>
                      <div style={passwordStrengthFillStyles(passwordStrength)}></div>
                    </div>
                    <small style={{ 
                      fontSize: theme.typography.fontSize.xs, 
                      color: passwordStrength.color,
                      fontWeight: theme.typography.fontWeight.medium
                    }}>
                      Password strength: {passwordStrength.text}
                    </small>
                  </>
                )}
              </div>

              <div style={inputGroupStyles}>
                <label style={labelStyles} htmlFor="confirm-password">
                  Confirm Password *
                </label>
                <div style={inputWrapperStyles}>
                  <input
                    id="confirm-password"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={8}
                    className="auth-input"
                    style={{...inputStyles, width: '100%', paddingRight: 'clamp(44px, 10vw, 54px)'}}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    className="auth-password-toggle"
                    style={passwordToggleStyles}
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                  >
                    {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
                {confirmPassword && (
                  <small style={{ 
                    fontSize: theme.typography.fontSize.xs, 
                    color: registerPassword === confirmPassword ? theme.colors.success : theme.colors.error,
                    fontWeight: theme.typography.fontWeight.medium
                  }}>
                    {registerPassword === confirmPassword ? '✓ Passwords match' : '✗ Passwords do not match'}
                  </small>
                )}
              </div>

              <div style={{
                padding: 'clamp(14px, 3vw, 20px)',
                background: `linear-gradient(135deg, ${theme.colors.surfaceAlt} 0%, ${theme.colors.surface} 100%)`,
                borderRadius: 'clamp(12px, 3vw, 16px)',
                border: `2px solid ${theme.colors.border}`,
                marginTop: 'clamp(4px, 1.5vw, 8px)',
                boxShadow: '0 4px 12px rgba(139, 69, 19, 0.08)',
              }}>
                <p style={{
                  fontSize: 'clamp(0.8125rem, 2vw, 0.875rem)',
                  fontWeight: theme.typography.fontWeight.bold,
                  color: theme.colors.primary,
                  marginBottom: 'clamp(12px, 3vw, 16px)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'clamp(6px, 1.5vw, 8px)',
                }}>
                  <span style={{ fontSize: 'clamp(1rem, 2.5vw, 1.25rem)' }}>💼</span>
                  Business Information (Optional)
                </p>

                <div style={{display: 'flex', flexDirection: 'column', gap: 'clamp(10px, 2.5vw, 14px)'}}>
                  <div style={inputGroupStyles}>
                    <label style={labelStyles} htmlFor="business-name">
                      Business Name
                    </label>
                    <input
                      id="business-name"
                      type="text"
                      value={businessName}
                      onChange={(e) => setBusinessName(e.target.value)}
                      className="auth-input"
                      style={inputStyles}
                      placeholder="Your Business LLC"
                    />
                  </div>

                  <div style={inputGroupStyles}>
                    <label style={labelStyles} htmlFor="tax-id">
                      Tax Permit #
                    </label>
                    <input
                      id="tax-id"
                      type="text"
                      value={taxId}
                      onChange={(e) => setTaxId(e.target.value)}
                      className="auth-input"
                      style={inputStyles}
                      placeholder=""
                    />
                    <small style={{ 
                      fontSize: theme.typography.fontSize.xs, 
                      color: theme.colors.textLight,
                      fontWeight: theme.typography.fontWeight.normal,
                      marginTop: '4px'
                    }}>
                      Please enter your seller's permit number or sales tax account number.
                    </small>
                  </div>

                  <div style={inputGroupStyles}>
                    <label style={labelStyles} htmlFor="resale-certificate">
                      Resale Certificate
                    </label>
                    <label 
                      htmlFor="resale-certificate" 
                      className="auth-file-label"
                      style={fileInputLabelStyles}
                    >
                      {resaleCertificate ? `📎 ${resaleCertificate.name}` : '📤 Click to upload file'}
                    </label>
                    <input
                      id="resale-certificate"
                      type="file"
                      onChange={handleFileChange}
                      accept="image/*,.pdf"
                      style={{display: 'none'}}
                    />
                    <small style={{ 
                      fontSize: 'clamp(0.75rem, 1.8vw, 0.8125rem)', 
                      color: theme.colors.textSecondary,
                      marginTop: 'clamp(4px, 1vw, 6px)',
                      display: 'block',
                      lineHeight: '1.4',
                    }}>
                      Upload certificate for wholesale pricing (PDF/image, max 5MB). Please upload your state's signature form. <strong>Note: A signature form is required for almost all states except Florida and Washington state.</strong>
                    </small>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="modern-auth-button"
                style={{ 
                  ...modernButtonStyles,
                  marginTop: theme.spacing.md,
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    Object.assign(e.currentTarget.style, modernButtonHoverStyles);
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(139, 69, 19, 0.3)';
                    e.currentTarget.style.background = `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primaryDark} 100%)`;
                  }
                }}
                onMouseDown={(e) => {
                  if (!loading) {
                    Object.assign(e.currentTarget.style, modernButtonActiveStyles);
                  }
                }}
                onMouseUp={(e) => {
                  if (!loading) {
                    Object.assign(e.currentTarget.style, modernButtonHoverStyles);
                  }
                }}
                onTouchStart={(e) => {
                  if (!loading) {
                    e.currentTarget.style.transform = 'scale(0.97)';
                    e.currentTarget.style.opacity = '0.9';
                  }
                }}
                onTouchEnd={(e) => {
                  if (!loading) {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.opacity = '1';
                  }
                }}
              >
                {loading ? (
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: theme.spacing.md }}>
                    <span className="loading-spinner"></span>
                    Creating Account...
                  </span>
                ) : (
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: theme.spacing.sm }}>
                    Create Account
                  </span>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default AuthModal;

